/*
  Maps claim API responses to the copy keys in model.en.json (errors.*).
  Codes come from subscription-portal-lambda (practice_handlers.py): POST /v1/claim and
  GET /v1/claim/{token}/preview.
*/

export type ClaimErrorCode =
  | 'already_claimed'
  | 'access_active'
  | 'expired'
  | 'refunded'
  | 'not_found'
  | 'no_account_id'
  | 'entitlement_error'
  | 'invalid_request'
  | 'unauthorized'
  | 'network'
  | 'unknown'

// Dates for the access_active message
export type ClaimErrorDetails = {
  activeUntil?: string
  linkExpiresAt?: string | null
}

// Preview statuses that block activation use the same message view.
export type ClaimMessageKey = ClaimErrorCode | 'claimed'

export type ClaimErrorAction = 'retry' | 'signInOther' | 'signIn' | null

const KNOWN_CODES: ClaimErrorCode[] = [
  'already_claimed',
  'access_active',
  'expired',
  'refunded',
  'not_found',
  'no_account_id',
  'entitlement_error',
  'invalid_request'
]

const STATUS_FALLBACK: { [status: number]: ClaimErrorCode } = {
  400: 'invalid_request',
  401: 'unauthorized',
  404: 'not_found',
  409: 'already_claimed',
  410: 'expired',
  502: 'entitlement_error'
}

export const getClaimErrorCode = (
  status?: number | null,
  code?: string | null
): ClaimErrorCode => {
  const normalized = (code || '').toLowerCase() as ClaimErrorCode
  if (KNOWN_CODES.includes(normalized)) {
    return normalized
  }

  // apisauce gives no status when the request never reached the server
  if (!status) {
    return 'network'
  }

  return STATUS_FALLBACK[status] || 'unknown'
}

/*
  APIService rejects every 401 with Error('INVALID_USER'); the parsed body is
  attached as err.responsePayload. A 401 with { code: 'no_account_id' } comes
  from the claim Lambda; any other 401 is the JWT authorizer rejecting the token.
*/
export const getClaimErrorCodeFromException = (
  err: unknown
): ClaimErrorCode => {
  const e = err as {
    message?: string
    responsePayload?: { code?: string } | null
  } | null

  if (e && e.message === 'INVALID_USER') {
    return e.responsePayload?.code === 'no_account_id'
      ? 'no_account_id'
      : 'unauthorized'
  }

  return 'network'
}

export const getErrorAction = (key: ClaimMessageKey): ClaimErrorAction => {
  switch (key) {
    case 'already_claimed':
    case 'access_active':
      return 'signInOther'
    case 'unauthorized':
      return 'signIn'
    case 'entitlement_error':
    case 'network':
    case 'unknown':
      return 'retry'
    default:
      return null
  }
}
